type GridPosition = [number, number]; // [col, row]

interface PatternOptions {
  isSmallScreen: boolean;
  itemCount: number;
}

/**
 * Returns an array of grid positions [col, row] for different layout patterns
 */
export const getHoneycombPattern = ({ isSmallScreen, itemCount }: PatternOptions): GridPosition[] => {
  // Small screen patterns (mobile)
  if (isSmallScreen && itemCount > 3) {
    return getSmallScreenPattern(itemCount);
  }
  
  // Desktop patterns
  switch (itemCount) {
    case 3:
      return [
        [0, 0],    // First column, center
        [1, -0.6], // Second column, upper
        [1, 0.6],  // Second column, lower
      ];
    case 4:
      return [
        [0, 0],    // First column, center
        [1, -0.6], // Second column, upper
        [1, 0.6],  // Second column, lower
        [2, 0],    // Third column, center
      ];
    case 5:
      return [
        [0, 0],     // First column, center
        [1, -0.6],  // Second column, upper
        [1, 0.6],   // Second column, lower
        [2, -1.2],  // Third column, upper
        [2, 1.2],   // Third column, lower
      ];
    case 6:
      return [
        [0, 0],     // First column, center
        [1, -0.6],  // Second column, upper
        [1, 0.6],   // Second column, lower
        [2, -1.2],  // Third column, upper
        [2, 0],     // Third column, middle
        [2, 1.2],   // Third column, lower
      ];
    case 7:
      return [
        [0, 1.2],   // First column, upper
        [1, -0.6],  // Second column, upper
        [1, 0.6],   // Second column, lower
        [2, -1.2],  // Third column, upper
        [2, 0],     // Third column, middle
        [2, 1.2],   // Third column, lower
        [0, -1.2],  // First column, lower
      ];
    case 8:
      return [
        [0, 0],     // First column, center
        [1, -0.6],  // Second column, upper
        [1, 0.6],   // Second column, lower
        [2, -1.2],  // Third column, upper
        [2, 0],     // Third column, middle
        [2, 1.2],   // Third column, lower
        [0, -1.2],  // First column, lower
        [0, 1.2],   // First column, upper
      ];
      
    default:
      // For other counts, create a generic pattern
      return createGenericPattern(itemCount);
  }
};

/**
 * Returns small screen patterns for different item counts
 */
const getSmallScreenPattern = (itemCount: number): GridPosition[] => {
  if (itemCount === 8) {
    return [
      [-1.3, 0],        // Position 1: Left, top
      [0, 0.8],   // Position 2: Right, top
      [-1.3, 1.6],      // Position 3: Middle left, middle
      [0, 2.4],   // Position 4: Middle right, middle  
      [-1.3, 3.2],      // Position 5: Left, bottom
      [0, 4],      // Position 6: Right, bottom
      [-1.3, 4.8],       // Position 7: Left, bottom
      [0, 5.6],    // Position 8: Right, bottom
      [-1.3, 6.4],       // Position 9: Left, bottom
    ];
  } else if (itemCount === 8) {
    return [
      [-1.3, 0],        // Position 1: Left, top
      [0, 0.8],   // Position 2: Right, top
      [-1.3, 1.6],      // Position 3: Middle left, middle
      [0, 2.4],   // Position 4: Middle right, middle  
      [-1.3, 3.2],      // Position 5: Left, bottom
      [0, 4],      // Position 6: Right, bottom
      [-1.3, 4.8],       // Position 7: Left, bottom
      [0, 5.6],    // Position 8: Right, bottom
    ];
  } else if (itemCount === 7) {
    return [
      [-1.3, 0],        // Position 1: Left, top
      [0, 0.8],   // Position 2: Right, top
      [-1.3, 1.6],      // Position 3: Middle left, middle
      [0, 2.4],   // Position 4: Middle right, middle  
      [-1.3, 3.2],      // Position 5: Left, bottom
      [0, 4],      // Position 6: Right, bottom
      [-1.3, 4.8],       // Position 7: Left, bottom
    ];
  } else if (itemCount === 6) {
    //  [1]       [2]
    //     [3]       [4]
    // [5]       [6]
    return [
      [-1.3, 0],        // Position 1: Left, top
      [0, 0.8],   // Position 2: Right, top
      [-1.3, 1.6],      // Position 3: Middle left, middle
      [0, 2.4],   // Position 4: Middle right, middle  
      [-1.3, 3.2],      // Position 5: Left, bottom
      [0, 4]      // Position 6: Right, bottom
    ];
  } else if (itemCount === 5) {
    // [1]       [2]
    //     [3]       [4]
    // [5]
    return [
      [-1.3, 0],        // Position 1: Left, top
      [0, 0.8],   // Position 2: Right, top
      [-1.3, 1.6],      // Position 3: Middle left, middle
      [0, 2.4],   // Position 4: Middle right, middle  
      [-1.3, 3.2]       // Position 5: Left, bottom
    ];
  } else if (itemCount === 4) {
    // [1]       [2]
    //     [3]       [4]
    return [
      [-1.3, 0],        // Position 1: Left, top
      [0, 0.8],   // Position 2: Right, top
      [-1.3, 1.6],      // Position 3: Middle left, middle
      [0, 2.4]    // Position 4: Middle right, middle
    ];
  } else {
    // Generic zigzag for other counts
    const positions: GridPosition[] = [];
    let row = 0;
    for (let i = 0; i < itemCount; i += 2) {
      if (i < itemCount) positions.push([0, row]);   // Left position
      if (i + 1 < itemCount) positions.push([1, row]); // Right position
      row++;
    }
    return positions;
  }
};

/**
 * Creates a generic pattern for any number of items
 */
const createGenericPattern = (itemCount: number): GridPosition[] => {
  // This is a placeholder for a more complex algorithm that could handle any number of items
  // For now, we'll implement a simple grid pattern
  const positions: GridPosition[] = [];
  const maxColumns = 3;
  
  for (let i = 0; i < itemCount; i++) {
    const col = i % maxColumns;
    const row = Math.floor(i / maxColumns);
    const rowOffset = col % 2 === 0 ? 0 : 0.5; // Offset every other column for honeycomb effect
    positions.push([col, row + rowOffset]);
  }
  
  return positions;
};